import { useChecklist } from '@tour-kit/checklists';
import { useTours } from '@tour-kit/react';
import { useEffect, useRef } from 'react';

import {
  GET_STARTED_CHECKLIST_ID,
  GET_STARTED_TASK,
  ORIENTATION_TOUR_ID,
} from '@/core/guidance/constants';
import { domainCompletedTaskIds } from '@/core/guidance/get-started-checklist';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import type { GetStartedDomainTruth } from '@/core/guidance/use-get-started-domain';

type GetStartedCompletionSyncProps = {
  domain: GetStartedDomainTruth;
  shareDone: boolean;
};

/**
 * TourKit only auto-completes `urlVisit` conditions. Domain + tour + share
 * completion are synced into checklist state here.
 */
export function GetStartedCompletionSync({
  domain,
  shareDone,
}: GetStartedCompletionSyncProps) {
  const { completeTask, exists } = useChecklist(GET_STARTED_CHECKLIST_ID);
  const { isTourCompleted, isTourSkipped } = useTours();
  const shareSignal = useGuidanceStore((s) => s.getStartedShareSignal);
  const lastShareSignal = useRef(0);

  useEffect(() => {
    if (!exists || !domain.isDomainReady) return;

    for (const taskId of domainCompletedTaskIds(domain)) {
      completeTask(taskId);
    }
  }, [exists, domain, completeTask]);

  useEffect(() => {
    if (!exists) return;
    if (
      isTourCompleted(ORIENTATION_TOUR_ID) ||
      isTourSkipped(ORIENTATION_TOUR_ID)
    ) {
      completeTask(GET_STARTED_TASK.meTour);
    }
  }, [exists, isTourCompleted, isTourSkipped, completeTask]);

  useEffect(() => {
    if (!exists) return;
    if (shareDone || shareSignal > lastShareSignal.current) {
      lastShareSignal.current = shareSignal;
      completeTask(GET_STARTED_TASK.meShare);
    }
  }, [exists, shareDone, shareSignal, completeTask]);

  return null;
}
